/**
 * Чи є поточний користувач адміністратором СВОЄЇ організації.
 *
 * Відповідь лежить у самому документі — у таблиці `Sys_user_attributes`, тій самій, куди
 * дивляться правила доступу через `user.Org.role`. Тому питаємо документ, а не сервер:
 * зайвий круг по мережі тут ні до чого, а джерело правди виходить одне з правилами.
 *
 * Потрібно це рівно для одного: показати кнопку імпорту адміну організації. Головний
 * адміністратор дістає її через `canEditStructure`, звичайний стюард не дістає взагалі.
 * [import-tablyts-nedostupnyy-dlia-rehionalnykh-korystuvachiv]
 */
import { GristDoc } from "app/client/components/GristDoc";
import { AppModel } from "app/client/models/AppModel";

const USER_ATTRIBUTES_TABLE_ID = "Sys_user_attributes";
const ORG_ADMIN_ROLE_VALUE = "admin";

function _normalizeEmail(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

export async function isRegionalOrgAdmin(gristDoc: GristDoc, appModel: AppModel): Promise<boolean> {
  const email = _normalizeEmail(
    appModel.currentUser?.email || appModel.currentUser?.loginEmail,
  );
  if (!email) {
    return false;
  }
  const docData = gristDoc.docData;
  if (!docData) {
    return false;
  }
  // ЗАВЖДИ довантажуємо, а не «якщо getTable повернув порожньо».
  //
  // `getTable` віддає ЗАРЕЄСТРОВАНУ таблицю навіть тоді, коли її рядки ще не завантажені:
  // `isLoaded` лишається false, а `getRowIds()` повертає порожньо. Перша версія перевіряла
  // `if (!table)` — і для зареєстрованої-але-незавантаженої таблиці не довантажувала нічого,
  // чесно нікого не знаходила й ховала кнопку від кожного адміна організації.
  //
  // Це той самий підводний камінь, що й на сервері (див. перевірку власності рядків), тільки
  // з іншого боку. `fetchTable` ідемпотентний: на завантаженій таблиці він нічого не робить.
  try {
    await docData.fetchTable(USER_ATTRIBUTES_TABLE_ID);
  } catch (err) {
    // Таблиці немає взагалі — поза шардованим режимом її не створюють. Це не помилка, просто «ні».
    return false;
  }
  const table = docData.getTable(USER_ATTRIBUTES_TABLE_ID);
  if (!table) {
    return false;
  }
  for (const rowId of table.getRowIds()) {
    if (_normalizeEmail(table.getValue(rowId, "email")) === email) {
      return _normalizeEmail(table.getValue(rowId, "role")) === ORG_ADMIN_ROLE_VALUE;
    }
  }
  return false;
}
